import { Clock, Loader2 } from 'lucide-react';
import type { ApplicationState, Channel, Interview } from '@/types';
import { useLanguage } from '@/contexts/LanguageContext';
import { resolveDisplayApplicationState } from '@/lib/pendingProcessing';

export const ScoreBadge = ({ score, size = 'sm' }: { score?: number; size?: 'sm' | 'lg' }) => {
  if (score === undefined || score === null) {
    return <span className="text-xs text-muted-foreground">—</span>;
  }

  let colorClass = 'bg-score-poor/10 text-score-poor';
  if (score >= 80) colorClass = 'bg-score-excellent/10 text-score-excellent';
  else if (score >= 60) colorClass = 'bg-score-good/10 text-score-good';
  else if (score >= 40) colorClass = 'bg-score-fair/10 text-score-fair';

  if (size === 'lg') {
    return (
      <div className={`inline-flex items-center justify-center h-14 w-14 rounded-full font-bold text-lg ${colorClass}`}>
        {score}
      </div>
    );
  }

  return (
    <span className={`inline-flex items-center rounded-md px-1.5 py-0.5 text-xs font-medium ${colorClass}`}>
      {score}
    </span>
  );
};

const stateStyle: Record<ApplicationState, string> = {
  APPLIED_NEW: 'bg-muted text-muted-foreground',
  PENDING_UPGRADE: 'bg-amber-500/10 text-amber-700 dark:text-amber-300 border border-amber-500/40',
  READY_TO_PROCESS: 'bg-muted text-muted-foreground border border-border',
  PIPELINE_RUNNING: 'bg-primary/10 text-primary animate-pulse-soft',
  AWAITING_DECISION: 'bg-warning/10 text-warning',
  PIPELINE_ERROR: 'bg-destructive/10 text-destructive',
  REJECTED: 'bg-muted text-muted-foreground line-through',
  HIRED: 'bg-success/10 text-success',
  OFFER_DECLINED: 'bg-warning/10 text-warning',
  WITHDRAWN: 'bg-muted text-muted-foreground',
};

const stateLabelKey: Record<ApplicationState, string> = {
  APPLIED_NEW: 'applications.new',
  PENDING_UPGRADE: 'application.pendingUpgrade.badge',
  READY_TO_PROCESS: 'application.readyToProcess.badge',
  PIPELINE_RUNNING: 'applications.processing',
  AWAITING_DECISION: 'applications.awaitingDecision',
  PIPELINE_ERROR: 'applications.reviewRequired',
  REJECTED: 'applications.rejected',
  HIRED: 'applications.hired',
  OFFER_DECLINED: 'applications.offerDeclined',
  WITHDRAWN: 'applications.withdrawn',
};

const interviewStatusStyle: Record<Interview['status'], string> = {
  Scheduled: 'bg-primary/10 text-primary',
  Updated: 'bg-sky-500/10 text-sky-700 dark:text-sky-400',
  Completed: 'bg-muted text-muted-foreground',
  Cancelled: 'bg-destructive/10 text-destructive',
};

const interviewStatusLabelKey: Record<Interview['status'], string> = {
  Scheduled: 'appDetail.interviewStatus.scheduled',
  Updated: 'appDetail.interviewStatus.updated',
  Completed: 'appDetail.interviewStatus.completed',
  Cancelled: 'appDetail.interviewStatus.cancelled',
};

/** Interview row status from API (Scheduled / Updated after reschedule / Completed when slot ended). */
export const InterviewStatusBadge = ({ status }: { status: Interview['status'] }) => {
  const { t } = useLanguage();
  return (
    <span
      className={`inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium whitespace-nowrap ${interviewStatusStyle[status]}`}
    >
      {t(interviewStatusLabelKey[status])}
    </span>
  );
};

function interviewScheduledMs(int: Interview): number {
  const t = Date.parse(int.scheduledAt);
  return Number.isNaN(t) ? 0 : t;
}

function interviewCreatedMs(int: Interview): number {
  if (!int.createdAt) return 0;
  const t = Date.parse(int.createdAt);
  return Number.isNaN(t) ? 0 : t;
}

/**
 * Interview row driving list substatus badges — prefer active slots (Scheduled/Updated)
 * over terminal ones (Cancelled/Completed), then most recently created among that pool.
 */
export function pickLatestInterview(interviews: Interview[] | undefined): Interview | null {
  const list = interviews?.filter(Boolean) ?? [];
  if (list.length === 0) return null;
  const active = list.filter((i) => i.status === 'Scheduled' || i.status === 'Updated');
  const pool = active.length > 0 ? active : list;
  return (
    [...pool].sort((a, b) => {
      const ca = interviewCreatedMs(a);
      const cb = interviewCreatedMs(b);
      if (cb !== ca) return cb - ca;
      return interviewScheduledMs(b) - interviewScheduledMs(a);
    })[0] ?? null
  );
}

const INTERVIEW_STATUS_SORT_RANK: Record<Interview['status'], number> = {
  Scheduled: 1,
  Updated: 2,
  Completed: 3,
  Cancelled: 4,
};

/** Sort key for interview column — status rank then scheduled time (empty last). */
export function getInterviewSortKey(interviews: Interview[] | undefined): string {
  const latest = pickLatestInterview(interviews);
  if (!latest) return 'z';
  const rank = String(INTERVIEW_STATUS_SORT_RANK[latest.status]).padStart(2, '0');
  return `${rank}-${latest.scheduledAt || ''}`;
}

/** Latest interview status only — lists + detail header (tab Interview still lists all rows). */
export const InterviewSubstatusBadges = ({ interviews }: { interviews?: Interview[] }) => {
  const latest = pickLatestInterview(interviews);
  if (!latest) {
    return <span className="text-muted-foreground text-xs tabular-nums">—</span>;
  }
  return (
    <InterviewStatusBadge status={latest.status} />
  );
};

/** Row status during bulk queue — stopping pass, then in-flight processing (toolbar stays enabled). */
export const ApplicationRowProcessBadge = ({
  state,
  stopping,
  processing,
  queued,
  stuck,
  hasProcessingCredits,
}: {
  state: ApplicationState;
  stopping?: boolean;
  processing?: boolean;
  queued?: boolean;
  stuck?: boolean;
  /** When true, PENDING_UPGRADE rows display as READY_TO_PROCESS until BE sync. */
  hasProcessingCredits?: boolean;
}) => {
  const { t } = useLanguage();
  const displayState = resolveDisplayApplicationState(state, hasProcessingCredits);
  if (stopping) {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-md px-2 py-0.5 text-xs font-medium whitespace-nowrap bg-amber-500/10 text-amber-700 dark:text-amber-300 border border-amber-500/30">
        <Loader2 className="h-3 w-3 shrink-0 animate-spin" aria-hidden />
        {t('applications.rowStopping')}
      </span>
    );
  }
  if (processing) {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-md px-2 py-0.5 text-xs font-medium whitespace-nowrap bg-primary/10 text-primary">
        <Loader2 className="h-3 w-3 shrink-0 animate-spin" aria-hidden />
        {t('applications.processing')}
      </span>
    );
  }
  if (queued) {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-md px-2 py-0.5 text-xs font-medium whitespace-nowrap bg-sky-500/10 text-sky-700 dark:text-sky-300 border border-sky-500/30">
        <Clock className="h-3 w-3 shrink-0" aria-hidden />
        {t('applications.inQueue')}
      </span>
    );
  }
  if (stuck) {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-md px-2 py-0.5 text-xs font-medium whitespace-nowrap bg-amber-500/10 text-amber-800 dark:text-amber-300 border border-amber-500/35">
        {t('applications.processingStuck')}
      </span>
    );
  }
  if (displayState === 'PIPELINE_RUNNING') {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-md px-2 py-0.5 text-xs font-medium whitespace-nowrap bg-primary/10 text-primary">
        <Loader2 className="h-3 w-3 shrink-0 animate-spin" aria-hidden />
        {t('applications.processing')}
      </span>
    );
  }
  return <StatusBadge state={displayState} />;
};

/** Application-level state only — interview lifecycle (scheduled / completed) is on each `Interview` row via `InterviewStatusBadge`. */
export const StatusBadge = ({ state }: { state: ApplicationState }) => {
  const { t } = useLanguage();
  return (
    <span
      className={`inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium whitespace-nowrap ${stateStyle[state]}`}
    >
      {t(stateLabelKey[state])}
    </span>
  );
};

const channelColors: Record<Channel, string> = {
  'LinkedIn': 'bg-[hsl(210,100%,45%)]/10 text-[hsl(210,100%,45%)]',
  'Company Website': 'bg-success/10 text-success',
  'Indeed': 'bg-[hsl(25,95%,53%)]/10 text-[hsl(25,95%,53%)]',
  'Glassdoor': 'bg-[hsl(170,75%,41%)]/10 text-[hsl(170,75%,41%)]',
  'InfoJobs': 'bg-[hsl(270,67%,47%)]/10 text-[hsl(270,67%,47%)]',
  'Referral': 'bg-warning/10 text-warning',
  'Internal': 'bg-muted text-muted-foreground',
  'Other': 'bg-muted text-muted-foreground',
};

export const ChannelBadge = ({ channel, label }: { channel: Channel; label?: string }) => {
  const display = label?.trim() || channel;
  return (
    <span className={`inline-flex items-center rounded-md px-1.5 py-0.5 text-xs font-medium whitespace-nowrap ${channelColors[channel] || channelColors.Other}`}>
      {display}
    </span>
  );
};

const posStatusConfig: Record<string, string> = {
  draft: 'bg-muted text-muted-foreground',
  active: 'bg-success/10 text-success',
  closed: 'bg-primary/10 text-primary',
  cancelled: 'bg-destructive/10 text-destructive',
};

const posStatusLabelKey: Record<string, string> = {
  draft: 'positions.draft',
  active: 'positions.active',
  closed: 'positions.closed',
  cancelled: 'positions.cancelled',
};

export const PositionStatusBadge = ({ status }: { status: string }) => {
  const { t } = useLanguage();
  return (
    <span className={`inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium whitespace-nowrap ${posStatusConfig[status] || posStatusConfig.draft}`}>
      {t(posStatusLabelKey[status] || 'positions.draft')}
    </span>
  );
};
